import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid } from 'uuid';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { datasetSharesTypes, datasetSharesActions, datasetSharesSelectors } from 'companion-app-components/flux/dataset-shares';
import { removeDialog } from 'companion-app-components/flux/root-ui/rootUiActions';
import { tracker } from 'companion-app-components/utils/core';
import { 
  DatasetShareAccessLevel, 
  AccountGroupWithAccess,
  AccountGroup,
} from 'companion-app-components/flux/account-groups/accountGroupsTypes';
import { 
  getAccountGroups,
} from 'companion-app-components/flux/account-groups/accountGroupsSelectors';
import { authSelectors } from 'companion-app-components/flux/auth';
import { Entitlement } from 'companion-app-components/flux/entitlements/entitlementsTypes';
import { hasCFOEntitlementForDataset } from 'companion-app-components/flux/entitlements/entitlementsUtils';

import { isEmail } from 'containers/App/Header/ProfileMenu/utils';
import { checkIfInviteExpired } from 'containers/ProfilePage/utils';
import AccountGroupsShare from 'components/AccountGroupsShare';

import AccessTypes from './AccessTypes';
import { DIALOG_TYPE_SHARE_FINANCES } from './actions';
import { getCFOEntityAccessTrackingProps } from './components/utils';
import { styles } from './styles';

const useStyles = makeStyles()(styles as Record<string, any>);

interface ShareFinancesDialogProps {
  id: string;
  name: string;
  entitlements: Entitlement[]
}

const ShareFinancesDialog: FC<ShareFinancesDialogProps> = ({ name, id, entitlements }) => {
  const accountGroups = useSelector(getAccountGroups);
  const currentDatasetId = useSelector(authSelectors.getDatasetId);
  const sharedWith = useSelector((state) => datasetSharesSelectors.getSharesByDatasetId(state, id))?.toList() ?? [];
  const actualSharedWithCount = sharedWith.reduce((count, sharing) => {
    const hasExpiredInvite = checkIfInviteExpired(sharing?.lastInviteSent);
    if (sharing?.state === datasetSharesTypes.DATASET_SHARING_STATES.ABANDONED) {
      return count;
    }
    if (sharing?.state === datasetSharesTypes.DATASET_SHARING_STATES.INVITED && hasExpiredInvite) {
      return count;
    }
    return count + 1;
  }, 0);

  const isCFOUser = hasCFOEntitlementForDataset(entitlements);

  const [accountGroupsState, setAccountGroupsState] = useState<AccountGroupWithAccess[]>([]);

  useEffect(() => {
    const accountGroupsWithAccess: AccountGroupWithAccess[] = accountGroups?.map((accountGroup) => ({
      ...(accountGroup.toJS() as AccountGroup),
      permission: 'NONE',
    })) ?? [];

    setAccountGroupsState(accountGroupsWithAccess);
  }, [accountGroups]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [fullAccess, setFullAccess] = useState(true);

  const dispatch = useDispatch();
  const { classes } = useStyles();

  const handleClose = () => {
    dispatch(removeDialog(DIALOG_TYPE_SHARE_FINANCES));
  };

  const handleInvite = () => {
    const write = accountGroupsState
      .filter((accountGroup) => accountGroup.permission === 'WRITE')
      .map((accountGroup) => accountGroup.id);
    const read = accountGroupsState
      .filter((accountGroup) => accountGroup.permission === 'READ')
      .map((accountGroup) => accountGroup.id);
    const itemAccessControl = {
      version: '1.0.0',
      resources: {
        ACCOUNT_GROUP: {
          write: fullAccess ? ['*'] : write,
          read: fullAccess ? ['*'] : read,
          delete: [],
        },
        Q17_ACCOUNT: {
          write: fullAccess ? ['*'] : write,
          read: fullAccess ? ['*'] : read,
          delete: [],
        },        
      },
    };   

    const createPayload = datasetSharesTypes.mkDatasetShare({
      clientId: uuid(),
      datasetId: id,
      granteeFirstName: firstName,
      granteeLastName: lastName,
      granteeEmail: email,
      state: datasetSharesTypes.DATASET_SHARING_STATES.INVITED,
      itemAccessControl,
    });
    
    dispatch(datasetSharesActions.createDatasetShare(createPayload));

    tracker.track(tracker.events.datasetSharing, {
      property: 'inviteClicked',
      ...(isCFOUser && {
        ...getCFOEntityAccessTrackingProps(fullAccess, accountGroupsState.length, read, write),
        total_shared: actualSharedWithCount,
      }),
    });

    handleClose();
  };

  const hasAtLeast1Entity = fullAccess || (!fullAccess && accountGroupsState.some(({ permission }) => permission !== 'NONE'));
  const inviteEnabled = firstName.trim() && lastName.trim() && email.trim() && isEmail(email) && hasAtLeast1Entity;
  
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };
  
  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter' && inviteEnabled) {
      handleInvite();
    }
  };

  const handleAccessChange = (groupId: string, selectedAccess: DatasetShareAccessLevel) => {
    const updated = accountGroupsState.map((accountGroup) => accountGroup.id === groupId ? { ...accountGroup, permission: selectedAccess } : accountGroup);
    setAccountGroupsState(updated);
  };

  const handleFullAccessChange = (event) => {
    setFullAccess(event.target.value === 'true');
  };

  return (
    <Dialog
      open
      sx={{ '& .MuiPaper-root': { padding: '0 20px' } }}
    >
      <DialogTitle
        className={classes.dialogTitleWrapper}
      >
        <Typography
          variant="h6" 
          className={classes.title}
        >
          Share finances
        </Typography>
        <Typography
          variant="body2"
          className={classes.textTertiary}
        >
          Enter details of the Quicken member who will share your financial data.
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        className={classes.closeButton}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogContent dividers>
        <Typography variant="h5" className={classes.weight400}>
          {name}
        </Typography>
        <Typography gutterBottom variant="caption" className={classes.textTertiary}>
          Sharing with Quicken member
        </Typography>
        <div className={classes.formWrapper}>
          <div>
            <Typography variant="caption">
              First Name
            </Typography>
            <TextField 
              variant="outlined"
              fullWidth
              size="small"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div>
            <Typography variant="caption">
              Last Name
            </Typography>
            <TextField 
              variant="outlined"
              fullWidth
              size="small"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Typography variant="caption">
            Email
          </Typography>
          <TextField 
            variant="outlined"
            fullWidth
            size="small"
            value={email}
            onChange={handleEmailChange}
            onKeyPress={handleEmailKeyPress}
          />
        </div>
        {isCFOUser &&
          <AccessTypes 
            fullAccess={fullAccess}
            handleFullAccessChange={handleFullAccessChange}
            accountGroups={accountGroupsState}
          />}
        {!isCFOUser &&
          <Alert 
            icon={false} 
            className={classes.alert}
          >
            By sharing your data, the invited member will have access to view and modify all your accounts, transactions, and other details.
          </Alert>}
        {!fullAccess && currentDatasetId === id && 
          <AccountGroupsShare 
            accountGroups={accountGroupsState}
            onAccessLevelChange={handleAccessChange}
          />}
      </DialogContent>
      <DialogActions
        className={classes.dialogAction}
      >
        <Button 
          onClick={handleClose}
          variant="outlined"
          size="small"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!inviteEnabled}
          onClick={handleInvite}
        >
          INVITE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareFinancesDialog;
